import axios from 'axios';
import { WebEntityName } from '../entities/types';
import {
  ADD_ENTITY,
  DELETE_ENTITY,
  EDIT_ENTITY,
  GET_ENTITY,
  LOADING_ENTITY,
  NOTIFY_MESSAGE_ENTITY,
  REFRESH_ENTITY
} from '../reducers/types';
import { AppDispatch, AppGetState } from '../store';
import { tokenConfig } from '../utils/header';
import { fetchDataOnStream } from '../utils/request';
import { loadQuery } from '../utils/utils';
import { handleError } from '../components/PopAlert';
import { MRT_RowData } from 'material-react-table';

export const getEntitiesAction =
  (
    entityName: WebEntityName,
    endpoint: string,
    {
      GETPath = '',
      query = {},
      fetchByNRows = 0,
      onParallel = 1,
      limitRows = 0
    }: {
      GETPath?: string;
      query?: any;
      fetchByNRows?: number;
      onParallel?: number;
      limitRows?: number;
    }
  ) =>
  () =>
  async (
    dispatch: AppDispatch,
    getState: AppGetState
  ): Promise<{ availablePages: number; totalItemsCount: number }> => {
    // Loading ....
    dispatch(setEntityLoading(entityName, true));

    try {
      const localQuery = await loadQuery(query);
      const localEndpoint = GETPath ? `${endpoint}/${GETPath}` : endpoint;

      const { passwordLessToken } = getState().auth.impersonation || {
        passwordLessToken: undefined
      };

      // Common Fetch
      await fetchDataOnStream(localEndpoint, {
        callback: (data) =>
          dispatch({
            type: GET_ENTITY,
            payload: data,
            entityName
          }),
        GETPath,
        query: localQuery,
        passwordLessToken,
        limitRows,
        fetchByNRows,
        onParallel
      });

      dispatch(setEntityLoading(entityName, false));
    } catch (error) {
      console.log(error);
      // In case of error clean the entity collaction
      dispatch({
        type: GET_ENTITY,
        payload: [],
        entityName
      });

      dispatch(setEntityLoading(entityName, false));
      handleError({ error, entityName, dispatch });
    }

    return { availablePages: 0, totalItemsCount: 0 };
  };

export const voidEntitiesAction =
  (entityName: WebEntityName) =>
  () =>
  async (dispatch: AppDispatch): Promise<{ availablePages: number; totalItemsCount: number }> => {
    // Loading ....
    dispatch(setEntityLoading(entityName, true));

    try {
      dispatch({
        type: GET_ENTITY,
        payload: [],
        entityName
      });
      dispatch(setEntityLoading(entityName, false));

      return { availablePages: 0, totalItemsCount: 0 };
    } catch (error) {
      console.log(error);
      handleError({ error, entityName, dispatch });
    }

    return { availablePages: 0, totalItemsCount: 0 };
  };

export const deleteEntityAction =
  (entityName: WebEntityName, endpoint: string) =>
  (id: string | string[]) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      const entityIds = Array.isArray(id) ? id : [id];
      const response = await axios.patch(
        `/api/${endpoint}`,
        { action: 'delete', entityIds },
        tokenConfig(getState)
      );
      const { deletedEntity } = response.data;

      dispatch({
        type: DELETE_ENTITY,
        payload: entityIds,
        entityName
      });

      return deletedEntity;
    } catch (error) {
      handleError({ error, entityName, dispatch });
    }
  };

export const addEntityAction =
  (entityName: WebEntityName, endpoint: string) =>
  <T>(entity: T | T[]) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    const entities = Array.isArray(entity) ? entity : [entity];

    try {
      const res = await axios.post(`/api/${endpoint}`, entities, tokenConfig(getState));

      dispatch({
        type: ADD_ENTITY,
        payload: res.data,
        entityName
      });

      return res;
    } catch (error) {
      handleError({ error, entityName, dispatch });
    }
  };

export const multiAddEntityAction =
  (entityName: WebEntityName, endpoint: string) =>
  <T>(entity: T | T[]) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    const entities = Array.isArray(entity) ? entity : [entity];

    try {
      const res = await axios.post(`/api/${endpoint}`, entities, tokenConfig(getState));

      dispatch({
        type: ADD_ENTITY,
        payload: res.data,
        entityName
      });

      return res;
    } catch (error) {
      handleError({ error, entityName, dispatch });
    }

    return null;
  };

export const editEntityAction =
  (entityName: WebEntityName, endpoint: string) =>
  <T>(entity: T) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      const data = (
        await axios.patch<{ data: MRT_RowData }>(
          `/api/${endpoint}`,
          { action: 'edit', entityBody: entity },
          tokenConfig(getState)
        )
      ).data;

      dispatch({
        type: EDIT_ENTITY,
        payload: data,
        entityName
      });
      return data;
    } catch (error) {
      handleError({ error, entityName, dispatch });
    }
  };

export const notifyMessageEntityAction =
  (entityName: WebEntityName) => (msg: string) => (dispatch: AppDispatch, _: AppGetState) => {
    dispatch({
      type: NOTIFY_MESSAGE_ENTITY,
      payload: msg,
      entityName
    });
  };

export const setEntityLoading = (
  entityName: WebEntityName,
  loading: boolean = true
): { type: typeof LOADING_ENTITY; payload: boolean; entityName: WebEntityName } => {
  return {
    type: LOADING_ENTITY,
    payload: loading,
    entityName
  };
};

export const refreshEntity = (entityName: WebEntityName) => async (dispatch: AppDispatch) => {
  dispatch({
    type: REFRESH_ENTITY,
    payload: null,
    entityName: entityName
  });
};
